import { Fragment, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, Phone } from 'lucide-react';
import Seo, { orgJsonLd } from '@/components/Seo';
import VideoOverlay from '@/components/VideoOverlay';
import { ServiceGlyph } from '@/components/diagrams';
import { Reveal, MaskedLine, Parallax, Magnetic, EASE } from '@/components/motion';
import { CtaSection } from '@/components/PageHero';
import { SERVICES, CREDIBILITY, PROCESS_STEPS, CONTACT, VIDEOS, POSTERS, IMG } from '@/content/site';

function ProcessStep({ step, i, progress, reduce }) {
  const start = i / 4;
  const opacity = useTransform(progress, [Math.max(0, start - 0.08), Math.min(1, start + 0.1)], [0.12, 1]);
  const y = useTransform(progress, [start, Math.min(1, start + 0.12)], [46, 0]);
  return (
    <motion.div
      className="relative flex gap-7 items-start py-7"
      style={{ willChange: 'opacity, transform', ...(reduce ? {} : { opacity, y }) }}
    >
      <div className="tl-node">{step.n}</div>
      <div>
        <h3 className="h3">{step.title}</h3>
        <p className="mt-2 text-[15px] leading-relaxed text-mist max-w-[46ch]">{step.desc}</p>
      </div>
    </motion.div>
  );
}

export default function Home() {
  const [active, setActive] = useState(0);
  const svc = SERVICES[active];

  return (
    <>
      <Seo
        title="KTK Advisors | Business Consulting & Cost Optimization"
        description="KTK Advisors helps businesses uncover cost-saving opportunities, improve efficiency, and pursue growth — with 40+ years of business experience and a Six Sigma Black Belt behind every engagement."
        path="/"
        jsonLd={orgJsonLd}
      />

      {/* ---------- Cinematic video hero ---------- */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="home-hero">
        <VideoOverlay src={VIDEOS.city} poster={POSTERS.city} tint="hero" />
        <div className="wrap relative pt-36 pb-40 lg:pt-40 lg:pb-48 w-full">
          <div>
            <Reveal y={10}>
              <div className="flex flex-wrap gap-3">
                <span className="badge-chip">40+ Years of Business Experience</span>
                <span className="badge-chip">Six Sigma Black Belt</span>
              </div>
            </Reveal>
            <h1 className="h1 mt-9">
              <MaskedLine delay={0.1}>Stronger margins.</MaskedLine>
              <MaskedLine delay={0.24}>Smarter operations.</MaskedLine>
              <MaskedLine delay={0.38}>
                <span>
                  More room <span className="gold-italic">to grow.</span>
                </span>
              </MaskedLine>
            </h1>
            <Reveal delay={0.55} y={16}>
              <p className="lede mt-8 max-w-[54ch]">
                KTK Advisors helps businesses uncover cost-saving opportunities,
                improve efficiency, and pursue growth—with experienced guidance
                and a practical approach to moving forward.
              </p>
            </Reveal>
            <Reveal delay={0.65} y={16}>
              <div className="mt-11 flex flex-wrap items-center gap-6">
                <Magnetic>
                  <Link to="/contact" data-testid="hero-schedule-consultation-button" className="btn btn-gold">
                    Schedule a Consultation
                    <ArrowRight size={17} />
                  </Link>
                </Magnetic>
                <Magnetic>
                  <Link to="/solutions" data-testid="hero-explore-solutions-link" className="btn btn-ghost">
                    Explore Our Solutions
                  </Link>
                </Magnetic>
              </div>
            </Reveal>
            <Reveal delay={0.75} y={12}>
              <ul className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3">
                {CREDIBILITY.map((c) => (
                  <li key={c} className="flex items-center gap-3">
                    <span className="cap-item !p-0"><span className="dot" /></span>
                    <span className="text-[12.5px] font-semibold tracking-wide text-ivory/75">{c}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 scroll-hint" aria-hidden="true">
          <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-mist">Scroll</span>
          <span className="line" />
        </div>
      </section>

      {/* ---------- Service command center ---------- */}
      <section className="relative bg-deep section overflow-hidden" data-testid="home-services-section">
        <div className="aura aura--gold" style={{ width: 480, height: 480, top: 60, right: -160 }} />
        <div className="wrap relative">
          <div className="grid lg:grid-cols-2 gap-8 items-end mb-14">
            <Reveal>
              <p className="eyebrow-mono">Our Solutions</p>
              <h2 className="h2 mt-6 max-w-[18ch]">Where could your business perform better?</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="body-copy text-mist max-w-[50ch] lg:ml-auto">
                Five practice areas, one way of working. Select an area to see
                where the leverage usually sits.
              </p>
            </Reveal>
          </div>

          <div className="grid lg:grid-cols-[0.92fr_1.08fr] gap-10 items-start">
            <div role="tablist" aria-label="Service areas" className="flex flex-col gap-1.5">
              {SERVICES.map((s, i) => (
                <button
                  key={s.slug}
                  role="tab"
                  id={`svc-tab-${i + 1}`}
                  aria-selected={active === i}
                  aria-controls="svc-panel"
                  data-testid={`home-service-tab-${i + 1}`}
                  className="tab-item"
                  onClick={() => setActive(i)}
                  onMouseEnter={() => setActive(i)}
                  onFocus={() => setActive(i)}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                      e.preventDefault();
                      const n = (active + 1) % SERVICES.length;
                      setActive(n);
                      document.getElementById(`svc-tab-${n + 1}`)?.focus();
                    }
                    if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                      e.preventDefault();
                      const n = (active - 1 + SERVICES.length) % SERVICES.length;
                      setActive(n);
                      document.getElementById(`svc-tab-${n + 1}`)?.focus();
                    }
                  }}
                >
                  <span className="n">{s.num}</span>
                  <span className="t">{s.title}</span>
                  <ArrowUpRight size={18} className="ml-auto text-goldbright opacity-0 transition-opacity duration-300" style={{ opacity: active === i ? 1 : 0 }} />
                </button>
              ))}
            </div>

            <div id="svc-panel" role="tabpanel" aria-live="polite" data-testid="home-service-panel">
              <motion.div
                key={svc.slug}
                initial={{ opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="glass overflow-hidden"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={svc.image}
                    alt={svc.imageAlt}
                    width="1400"
                    height="900"
                    loading="lazy"
                    className="w-full h-full object-cover"
                    style={{ filter: 'saturate(0.8) brightness(0.85)' }}
                  />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(7,29,24,0.25), rgba(11,38,32,0.95))' }} />
                  <ServiceGlyph name={svc.slug} className="absolute right-6 bottom-4 w-16 h-16" />
                </div>
                <div className="p-9">
                  <p className="font-mono text-[10px] uppercase tracking-[0.26em] text-goldbright">{svc.num}</p>
                  <h3 className="h3 mt-3">{svc.title}</h3>
                  <p className="mt-4 text-[15px] leading-relaxed text-mist">{svc.panel}</p>
                  <Link to={svc.path} data-testid={`home-service-panel-link-${active + 1}`} className="link-gold mt-7">
                    Explore this service
                    <span className="ar">→</span>
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Gold connector band ---------- */}
      <section className="relative bg-card border-y border-gold/15 py-20" data-testid="home-connector-band">
        <div className="wrap">
          <Reveal>
            <p className="eyebrow-mono">The Common Thread</p>
            <h2 className="h2 mt-6 max-w-[24ch]">Five areas. One outcome worth measuring.</h2>
          </Reveal>
          <div className="flow mt-14">
            {[
              { n: '01', label: 'Revenue collected' },
              { n: '02', label: 'Benefits structured' },
              { n: '03', label: 'Technology rightsized' },
              { n: '04', label: 'Shipping recovered' },
              { n: '05', label: 'Growth measured' },
            ].map((s, i) => (
              <Fragment key={s.n}>
                <motion.div
                  className="flow__node"
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6, delay: i * 0.12, ease: EASE }}
                >
                  <span className="flow__step-n">{s.n}</span>
                  <span className="flow__label block">{s.label}</span>
                </motion.div>
                {i < 4 && (
                  <div className="flow__arrow" aria-hidden="true">
                    <div className="flow__dash" />
                  </div>
                )}
              </Fragment>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-10 text-[15px] text-mist max-w-[60ch]">
              Each engagement ends the same way: with money kept, money recovered,
              or revenue set in motion — and a clear review of what it was worth.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ---------- Pinned process ---------- */}
      <PinnedProcess />
      <div className="lg:hidden bg-deep section">
        <div className="wrap">
          <p className="eyebrow-mono">Our Approach</p>
          <h2 className="h2 mt-6 max-w-[18ch]">A clear path from opportunity to action.</h2>
          <div className="relative mt-12">
            <div className="absolute left-[21px] top-2 bottom-2 w-px bg-gold/25" />
            {PROCESS_STEPS.map((s) => (
              <div key={s.n} className="relative flex gap-6 items-start py-6">
                <div className="tl-node">{s.n}</div>
                <div>
                  <h3 className="h3">{s.title}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-mist max-w-[48ch]">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <Link to="/our-approach" data-testid="home-approach-link" className="link-gold mt-8">
            See how we work together
            <span className="ar">→</span>
          </Link>
        </div>
      </div>

      {/* ---------- Founder spotlight ---------- */}
      <section className="relative bg-deep section overflow-hidden" data-testid="home-founder-section">
        <div className="aura aura--emerald" style={{ width: 620, height: 620, bottom: -240, left: -200 }} />
        <div className="wrap relative grid lg:grid-cols-[2fr_3fr] gap-16 items-center">
          <div className="relative">
            <Parallax amount={16}>
              <div className="tick-frame rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(178,139,67,0.35)' }} data-testid="founder-portrait-detail">
                <span className="tick tick--tl" /><span className="tick tick--br" />
                <img
                  src="/images/founder-portrait.jpg"
                  alt={`${CONTACT.founder}, Founder and CEO of KTK Advisors`}
                  width="1114"
                  height="1412"
                  loading="lazy"
                  className="w-full aspect-[4/5] object-cover"
                  style={{ objectPosition: '50% 12%', transform: 'scale(1.16)', transformOrigin: '50% 18%' }}
                />
              </div>
            </Parallax>
          </div>
          <div>
            <Reveal>
              <p className="eyebrow-mono">Your Advisor</p>
              <h2 className="h2 mt-6 max-w-[18ch]">Experience that connects the dots.</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="measure mt-8 space-y-6 body-copy text-[16.5px] text-ivory/85">
                <p>
                  With over 40 years of business experience and a Six Sigma Black
                  Belt credential, {CONTACT.founder} brings a practical, disciplined
                  approach to every engagement—identifying inefficiencies,
                  uncovering overlooked savings, and helping owners make informed
                  decisions about their operations.
                </p>
                <p className="text-mist">
                  Operational experience matters because cost and revenue problems
                  rarely respect department lines. Billing affects collections.
                  Benefits affect payroll. Technology and shipping affect every
                  invoice. An advisor who has seen how the pieces fit can connect
                  the dots faster—and point you to the opportunities worth pursuing.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="font-display italic text-[30px] text-goldbright mt-10">{CONTACT.founder}</p>
              <p className="font-mono text-[10.5px] uppercase tracking-[0.26em] text-mist mt-2">
                {CONTACT.founderTitle}, KTK Advisors
              </p>
              <Link to="/about" data-testid="home-about-link" className="link-gold mt-9">
                More about KTK Advisors
                <span className="ar">→</span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------- Terms video band ---------- */}
      <section className="relative overflow-hidden border-y border-gold/15" data-testid="home-terms-section">
        <VideoOverlay src={VIDEOS.gold} poster={POSTERS.gold} tint="soft" />
        <div className="wrap section relative grid lg:grid-cols-[1fr_2fr] gap-12">
          <Reveal>
            <p className="eyebrow-mono">Engagement Terms</p>
          </Reveal>
          <div>
            <Reveal delay={0.08}>
              <h2 className="h2 max-w-[16ch]">Aligned with your results.</h2>
              <p className="lede mt-7 max-w-[58ch]">
                For qualifying cost-reduction engagements, compensation is tied to
                the savings achieved. KTK Advisors explains the scope, fees, and
                expectations before you move forward.
              </p>
              <Link to="/our-approach" data-testid="home-terms-link" className="link-gold mt-9">
                How engagements work
                <span className="ar">→</span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <CtaSection
        title="Let's find the opportunities worth pursuing."
        copy="Tell us a little about your business and where you suspect there's room to do better. The first conversation is a consultation—clear, practical, and focused on your priorities."
        buttonLabel="Start a Conversation"
        buttonTo="/contact"
        testid="home-cta-button"
      />
    </>
  );
}

function PinnedProcess() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] });
  return (
    <div ref={ref} className="hidden lg:block relative h-[330vh] bg-deep" data-testid="home-process-pinned">
      <div className="sticky top-0 h-screen flex items-center overflow-hidden">
        <div className="wrap grid grid-cols-[0.9fr_1.1fr] gap-20 w-full items-center">
          <div>
            <p className="eyebrow-mono">Our Approach</p>
            <h2 className="h2 mt-6 max-w-[18ch]">A clear path from opportunity to action.</h2>
            <p className="lede mt-6 max-w-[40ch]">
              Four stages, one advisor. The path draws itself as you scroll.
            </p>
            <Link to="/our-approach" data-testid="home-approach-link" className="link-gold mt-9">
              See how we work together
              <span className="ar">→</span>
            </Link>
          </div>
          <div className="relative">
            <div className="absolute left-[21px] top-4 bottom-4 w-px bg-gold/20" aria-hidden="true" />
            <motion.div
              className="absolute left-[21px] top-4 bottom-4 w-px origin-top"
              style={{
                scaleY: reduce ? 1 : scrollYProgress,
                background: 'linear-gradient(180deg, #D4AF37, #B28B43)',
              }}
              aria-hidden="true"
            />
            {PROCESS_STEPS.map((s, i) => (
              <ProcessStep key={s.n} step={s} i={i} progress={scrollYProgress} reduce={reduce} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
